import SubjectIcon from "@/app/components/logos/SubjectIcon";
import {
  convertStripeDecimalPriceToPrice,
  convertStripePriceToMonthlyPrice,
  prettyCurrency,
} from "@/lib/stripe/formatters";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { getPricePeriodSuffix } from "@/lib/utils/subscription";
import {
  Box,
  Button,
  Divider,
  rgba,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import Link from "next/link";
import { CreateMembershipFormSubjectMeta } from "../../register/membership/page";
import classes from "./pricing-card.module.css";

interface PricingCardProps {
  title: string;
  description: string;
  price: number;
  currency: string;
  billingPeriod: string;
  isAllAccess: boolean;
  subjectMeta?: CreateMembershipFormSubjectMeta;
}

export default function PricingCard({
  title,
  description,
  price,
  currency,
  billingPeriod,
  isAllAccess,
  subjectMeta,
}: PricingCardProps) {
  const theme = useMantineTheme();

  return (
    <Box className={classes.card}>
      {/* Header */}
      <Stack gap="xs">
        <Text className={classes.cardTitle}>{title}</Text>
        <Text className={classes.description}>{description}</Text>
      </Stack>

      {/* Price */}
      <Text className={classes.price}>
        <span className={classes.priceCurrency}>
          {prettyCurrency(currency)}
        </span>
        {convertStripePriceToMonthlyPrice(price, billingPeriod)}

        <span className={classes.pricePeriod}>/month</span>
      </Text>

      <Divider />

      {/* Subjects Section - More Prominent */}
      <Box className={classes.subjectsSection}>
        <Text className={classes.subjectsSectionTitle}>
          {isAllAccess ? "Subjects Included" : "Select Multiple From"}
        </Text>
        {subjectMeta?.map((subject) => {
          const subjectColorIndex = getSubjectColorIndex(subject.subjectName);
          return (
            <Box
              key={subject.documentId}
              className={classes.subjectIconWrapper}
              style={
                {
                  "--subject-bg": rgba(
                    theme.colors[subjectColorIndex][5],
                    0.02
                  ),
                  "--subject-border": rgba(
                    theme.colors[subjectColorIndex][5],
                    0.1
                  ),
                } as React.CSSProperties
              }
            >
              <SubjectIcon subjectColorIndex={subjectColorIndex} size="sm" />
              <Text size="sm" fw={500} c={`${subjectColorIndex}.7`}>
                {subject.subjectName}
              </Text>
            </Box>
          );
        })}
      </Box>

      <Divider />

      <Stack gap="md">
        <Button
          color="darkblue"
          variant="white"
          size="lg"
          fullWidth
          className={classes.startMembershipButton}
          component={Link}
          href="/register"
        >
          Get Started
        </Button>
        <Text className={classes.priceSubtitle}>
          {billingPeriod === "month-1" ? (
            <>&nbsp;</>
          ) : (
            `${prettyCurrency(currency)}${convertStripeDecimalPriceToPrice(
              price,
              true
            )} billed ${getPricePeriodSuffix(billingPeriod).replace(
              "/",
              " every "
            )}`
          )}
        </Text>
      </Stack>
    </Box>
  );
}
